<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';

import PageLayout from 'dashboard/components-next/pilot/PageLayout.vue';
import CreateAssistantDialog from 'dashboard/components-next/pilot/pageComponents/assistant/CreateAssistantDialog.vue';
import AssistantPageEmptyState from 'dashboard/components-next/pilot/pageComponents/emptyStates/AssistantPageEmptyState.vue';
import AssistantCard from 'dashboard/components-next/pilot/pageComponents/assistant/AssistantCard.vue';
import FeatureSpotlightPopover from 'dashboard/components-next/feature-spotlight/FeatureSpotlightPopover.vue';
import AssistantsListControls from 'dashboard/components-next/pilot/pageComponents/assistant/AssistantsListControls.vue';
import DeleteDialog from 'dashboard/components-next/pilot/pageComponents/DeleteDialog.vue';
import BulkDeleteDialog from 'dashboard/components-next/pilot/pageComponents/BulkDeleteDialog.vue';
import BulkSelectBar from 'dashboard/components-next/pilot/assistant/BulkSelectBar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import AssistantsFilter from 'dashboard/components-next/filter/AssistantsFilter.vue';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

// 导入 SVG 图片
import assistantPopoverLight from 'assets/images/dashboard/pilot/assistant-popover-light.svg';
import assistantPopoverDark from 'assets/images/dashboard/pilot/assistant-popover-dark.svg';

const { isOnChatwootCloud } = useAccount();
const { t } = useI18n();
const store = useStore();
const router = useRouter();

const dialogType = ref('');
const uiFlags = useMapGetter('pilotAssistants/getUIFlags');
const assistants = useMapGetter('pilotAssistants/getRecords');
const assistantsMeta = useMapGetter('pilotAssistants/getMeta');
const isFetching = computed(() => uiFlags.value.fetchingList);

// 为 meta 提供默认值
const assistantsMetaSafe = computed(
  () =>
    assistantsMeta.value || {
      totalCount: 0,
      page: 1,
      pageSize: 25,
    }
);

const assistantsArray = computed(() => assistants.value || []);
const isEmpty = computed(() => !assistantsArray.value.length);
const isSearching = computed(() => searchValue.value.trim() !== '');
const isFiltering = computed(() => hasAppliedFilters.value);
const showEmptyState = computed(
  () => isEmpty.value && !isSearching.value && !isFiltering.value
);
const showNoResults = computed(
  () => isEmpty.value && (isSearching.value || isFiltering.value)
);

// 搜索和排序状态
const searchValue = ref('');
const sortBy = ref('created_at');
const sortOrder = ref('-'); // '' 表示升序，'-' 表示降序

// 筛选状态
const isFilterOpen = ref(false);
const appliedFilters = ref([]);
const filterQuery = ref([]);

let filterIdCounter = 0;
// 默认筛选条件
const DEFAULT_FILTER = {
  attributeKey: 'name',
  filterOperator: 'contains',
  values: '',
  queryOperator: 'and',
  attributeModel: 'standard',
};

// 批量选择
const bulkSelectedIds = ref(new Set());
const hoveredCard = ref(null);
const openMenuId = ref(null);

const selectedAssistant = ref(null);
const deleteAssistantDialog = ref(null);
const bulkDeleteDialog = ref(null);
const createAssistantDialog = ref(null);

// 防抖搜索
let searchDebounceTimer = null;
const handleSearchChange = value => {
  searchValue.value = value;
  clearTimeout(searchDebounceTimer);
  searchDebounceTimer = setTimeout(() => {
    fetchAssistants(1);
  }, 500);
};

const handleSortChange = ({ sort, order }) => {
  sortBy.value = sort;
  sortOrder.value = order;
  fetchAssistants(1);
};

// 筛选相关函数
const handleToggleFilter = () => {
  isFilterOpen.value = !isFilterOpen.value;
  if (isFilterOpen.value && filterQuery.value.length === 0) {
    filterQuery.value = [{ ...DEFAULT_FILTER, id: filterIdCounter++ }];
  }
};

const handleCloseFilter = () => {
  isFilterOpen.value = false;
};

const handleClearFilters = () => {
  appliedFilters.value = [];
  filterQuery.value = [{ ...DEFAULT_FILTER, id: filterIdCounter++ }];
  fetchAssistants(1);
};

const handleApplyFilter = filters => {
  appliedFilters.value = filters;
  fetchAssistants(1);
  isFilterOpen.value = false;
};

const hasAppliedFilters = computed(() => appliedFilters.value.length > 0);

// 批量选择相关
const buildSelectedCountLabel = computed(() => {
  const count = assistantsArray.value.length;
  const isAllSelected = bulkSelectedIds.value.size === count && count > 0;
  return isAllSelected
    ? t('PILOT.ASSISTANTS.UNSELECT_ALL', { count })
    : t('PILOT.ASSISTANTS.SELECT_ALL', { count });
});

const selectedCountLabel = computed(() => {
  return t('PILOT.ASSISTANTS.SELECTED', {
    count: bulkSelectedIds.value.size,
  });
});

const handleCardHover = (isHovered, id) => {
  hoveredCard.value = isHovered ? id : null;
};

const handleCardSelect = (id, isSelected) => {
  const selected = new Set(bulkSelectedIds.value);
  if (isSelected) {
    selected.add(id);
  } else {
    selected.delete(id);
  }
  bulkSelectedIds.value = selected;
};

const handleMenuOpen = id => {
  openMenuId.value = id;
};

const handleMenuClose = () => {
  openMenuId.value = null;
};

// 批量操作
const handleBulkPublish = async () => {
  try {
    const ids = Array.from(bulkSelectedIds.value);
    await Promise.all(
      ids.map(id =>
        store.dispatch('pilotAssistants/update', {
          id,
          data: { status: 'published' },
        })
      )
    );
    useAlert(
      t('PILOT.ASSISTANTS.BULK_PUBLISH.SUCCESS_MESSAGE', { count: ids.length })
    );
    bulkSelectedIds.value = new Set();
  } catch (error) {
    const errorMessage =
      error?.message || t('PILOT.ASSISTANTS.BULK_PUBLISH.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleBulkUnpublish = async () => {
  try {
    const ids = Array.from(bulkSelectedIds.value);
    await Promise.all(
      ids.map(id =>
        store.dispatch('pilotAssistants/update', {
          id,
          data: { status: 'draft' },
        })
      )
    );
    useAlert(
      t('PILOT.ASSISTANTS.BULK_UNPUBLISH.SUCCESS_MESSAGE', {
        count: ids.length,
      })
    );
    bulkSelectedIds.value = new Set();
  } catch (error) {
    const errorMessage =
      error?.message || t('PILOT.ASSISTANTS.BULK_UNPUBLISH.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleBulkDelete = () => {
  bulkDeleteDialog.value.dialogRef.open();
};

const onBulkDeleteSuccess = () => {
  const hasNoAssistantsLeft = assistantsArray.value.length === 0;
  const currentPage = assistantsMetaSafe.value.page;

  if (hasNoAssistantsLeft) {
    if (currentPage > 1) {
      fetchAssistants(currentPage - 1);
    } else {
      fetchAssistants(1);
    }
  }
  bulkSelectedIds.value = new Set();
};

const handleAction = async ({ action, id }) => {
  selectedAssistant.value = assistantsArray.value.find(
    assistant => assistant.id === id
  );

  if (!selectedAssistant.value) return;

  if (action === 'delete') {
    nextTick(() => {
      deleteAssistantDialog.value.dialogRef.open();
    });
  }
};

const fetchAssistants = (page = 1) => {
  // 如果有筛选条件，使用 filter API
  if (hasAppliedFilters.value) {
    const sortAttr =
      sortOrder.value === '-' ? `-${sortBy.value}` : sortBy.value;
    // 转换驼峰式键名到下划线式
    const snakeCaseFilters = useSnakeCase(
      JSON.parse(JSON.stringify(appliedFilters.value))
    );
    const queryPayload = filterQueryGenerator(snakeCaseFilters);
    store.dispatch('pilotAssistants/filter', {
      page,
      sortAttr,
      queryPayload,
      search: searchValue.value, // 传递搜索参数
    });
    return;
  }

  // 否则使用常规的 get API
  const params = { page };

  if (searchValue.value) {
    params.search = searchValue.value;
  }

  // Chatwoot 排序格式：'-created_at' 表示降序，'created_at' 表示升序
  if (sortBy.value) {
    const sortPrefix = sortOrder.value === '-' ? '-' : '';
    params.sort = `${sortPrefix}${sortBy.value}`;
  }

  store.dispatch('pilotAssistants/get', params);
};

const onPageChange = page => fetchAssistants(page);

// 加载助手列表
onMounted(() => {
  fetchAssistants();
});

const handleCreate = () => {
  dialogType.value = 'create';
  nextTick(() => createAssistantDialog.value.dialogRef.open());
};

const handleCreateClose = () => {
  dialogType.value = '';
  selectedAssistant.value = null;
};

const handleAfterCreate = newAssistant => {
  // Navigate to the assistant config page with the new assistant ID
  if (newAssistant?.id) {
    router.push({
      name: 'pilot_assistant_config_section',
      params: {
        accountId: router.currentRoute.value.params.accountId,
        assistantId: newAssistant.id,
        section: 'role',
      },
    });
  }
};

const handleCardClick = assistant => {
  // 跳转到新的配置页面
  router.push({
    name: 'pilot_assistant_config',
    params: {
      accountId: router.currentRoute.value.params.accountId,
      assistantId: assistant.id,
    },
  });
};

const onDeleteSuccess = () => {
  if (assistantsArray.value.length === 0 && assistantsMetaSafe.value.page > 1) {
    onPageChange(assistantsMetaSafe.value.page - 1);
  }
};
</script>

<template>
  <PageLayout
    :header-title="$t('PILOT.ASSISTANTS.HEADER')"
    :button-label="$t('PILOT.ASSISTANTS.ADD_NEW')"
    :button-policy="['administrator']"
    :total-count="assistantsMetaSafe.totalCount"
    :current-page="assistantsMetaSafe.page"
    :show-pagination-footer="
      !isFetching && !showEmptyState && !!assistantsArray.length
    "
    :is-fetching="isFetching"
    :is-empty="showEmptyState"
    :show-assistant-switcher="false"
    @update:current-page="onPageChange"
    @click="handleCreate"
  >
    <template #subHeader>
      <!-- 批量操作条和搜索筛选工具条 - 仅在有数据时显示 -->
      <div v-show="!showEmptyState">
        <!-- 批量操作条 -->
        <BulkSelectBar
          v-model="bulkSelectedIds"
          :all-items="assistantsArray"
          :select-all-label="buildSelectedCountLabel"
          :selected-count-label="selectedCountLabel"
          :delete-label="$t('PILOT.ASSISTANTS.BULK_DELETE_BUTTON')"
          class="w-full"
        >
          <template #actions>
            <div class="flex items-center gap-2">
              <Button
                :label="$t('PILOT.ASSISTANTS.BULK_PUBLISH.BUTTON')"
                icon="i-lucide-upload-cloud"
                size="sm"
                ghost
                blue
                @click="handleBulkPublish"
              />
              <Button
                :label="$t('PILOT.ASSISTANTS.BULK_UNPUBLISH.BUTTON')"
                icon="i-lucide-download-cloud"
                size="sm"
                ghost
                blue
                @click="handleBulkUnpublish"
              />
              <Button
                :label="$t('PILOT.ASSISTANTS.BULK_DELETE_BUTTON')"
                icon="i-lucide-trash"
                size="sm"
                ghost
                blue
                @click="handleBulkDelete"
              />
            </div>
          </template>
        </BulkSelectBar>

        <!-- 控制行：搜索和排序 -->
        <div v-show="bulkSelectedIds.size === 0" class="relative w-full">
          <AssistantsListControls
            key="list-controls"
            :search-value="searchValue"
            :sort-by="sortBy"
            :sort-order="sortOrder"
            :show-filter="true"
            :is-filter-open="isFilterOpen"
            @update:search-value="handleSearchChange"
            @update:sort="handleSortChange"
            @click-filter="handleToggleFilter"
          />

          <!-- 筛选弹窗 -->
          <div v-if="isFilterOpen" class="absolute top-full left-0 z-50 mt-2">
            <AssistantsFilter
              v-model="filterQuery"
              @apply-filter="handleApplyFilter"
              @close="handleCloseFilter"
              @clear-filters="handleClearFilters"
            />
          </div>
        </div>
      </div>
    </template>
    <template #knowMore>
      <FeatureSpotlightPopover
        :button-label="$t('PILOT.HEADER_KNOW_MORE')"
        :title="$t('PILOT.ASSISTANTS.EMPTY_STATE.FEATURE_SPOTLIGHT.TITLE')"
        :note="$t('PILOT.ASSISTANTS.EMPTY_STATE.FEATURE_SPOTLIGHT.NOTE')"
        :hide-actions="!isOnChatwootCloud"
        :fallback-thumbnail="assistantPopoverLight"
        :fallback-thumbnail-dark="assistantPopoverDark"
        learn-more-url=""
      />
    </template>
    <template #emptyState>
      <AssistantPageEmptyState @click="handleCreate" />
    </template>

    <template #body>
      <!-- 搜索无结果提示 -->
      <div
        v-if="showNoResults"
        class="flex flex-col items-center justify-center py-16 text-center"
      >
        <div
          class="i-lucide-search-x text-6xl text-n-slate-7 dark:text-n-slate-4 mb-4"
        />
        <h3
          class="text-xl font-semibold text-n-slate-12 dark:text-n-slate-1 mb-2"
        >
          {{ $t('PILOT.ASSISTANTS.SEARCH.NO_RESULTS') }}
        </h3>
        <p class="text-n-slate-11 dark:text-n-slate-5">
          {{ $t('PILOT.ASSISTANTS.SEARCH.TRY_DIFFERENT_KEYWORDS') }}
        </p>
        <button
          v-if="isFiltering"
          class="mt-4 px-4 py-2 bg-n-blue-6 text-white rounded-lg hover:bg-n-blue-7 transition-colors"
          @click="handleClearFilters"
        >
          {{ $t('PILOT.ASSISTANTS.FILTER.BUTTONS.CLEAR_FILTERS') }}
        </button>
      </div>

      <!-- 助手卡片列表 -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AssistantCard
          v-for="assistant in assistantsArray"
          :key="assistant.id"
          :assistant="assistant"
          :is-selected="bulkSelectedIds.has(assistant.id)"
          :selectable="hoveredCard === assistant.id || bulkSelectedIds.size > 0"
          :show-menu="!bulkSelectedIds.has(assistant.id)"
          :open-menu-id="openMenuId"
          @click="handleCardClick"
          @action="handleAction"
          @menu-open="handleMenuOpen"
          @menu-close="handleMenuClose"
          @select="isSelected => handleCardSelect(assistant.id, isSelected)"
          @hover="isHovered => handleCardHover(isHovered, assistant.id)"
        />
      </div>
    </template>

    <!-- Pilot features are accessible to all users, no paywall needed -->
    <!-- <template #paywall>
      <PilotPaywall />
    </template> -->

    <CreateAssistantDialog
      v-if="dialogType"
      ref="createAssistantDialog"
      :type="dialogType"
      :selected-assistant="selectedAssistant"
      @close="handleCreateClose"
      @created="handleAfterCreate"
    />

    <DeleteDialog
      v-if="selectedAssistant"
      ref="deleteAssistantDialog"
      type="Assistant"
      translation-key="ASSISTANTS"
      :entity="selectedAssistant"
      @delete-success="onDeleteSuccess"
    />

    <BulkDeleteDialog
      ref="bulkDeleteDialog"
      :bulk-ids="Array.from(bulkSelectedIds)"
      type="Assistant"
      translation-key="ASSISTANTS"
      @delete-success="onBulkDeleteSuccess"
    />
  </PageLayout>
</template>
